import { useEffect } from 'react'

import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect, DataClass, DataItem } from 'scrivito'

import { getTitleKey } from './getTitleKey'
import { Icon } from '@/Atomic/Atoms/Icon'
import { GlobalLoadingSpinner } from '@/Atomic/Atoms/Spinner'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { BusinessCardScanButton } from '@/Components/Contact/businessCardOcr'
import { DataClassName } from '@/Data/const'
import { useBusinessCardOcr } from '@/hooks/useBusinessCardOcr'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'

interface DataClassDrawerProps {
  dataClass: DataClass
  isOpen: boolean
  close: () => void
  onCreated?: (dataItem: DataItem) => void
  navigateOnCreated?: boolean
  attributeOptions?: {
    [attribute: string]: {
      default?: string
      readOnly?: boolean
      limitEnumValuesTo?: string[]
    }
  }
}

export const DataClassDrawer: React.FC<DataClassDrawerProps> = connect(
  ({
    dataClass,
    isOpen,
    close,
    onCreated,
    navigateOnCreated,
    attributeOptions,
  }) => {
    const { t } = useTranslation()

    const dataClassName = dataClass.name()!
    const addCanvasTitle = `${t(getTitleKey(dataClassName))}`

    const { methods, onFormSubmit, handleSave } = useDataClassFormHandler(
      dataClass,
      {
        prefill: { enabled: isOpen },
        onCreated: (dataItem) => {
          onCreated?.(dataItem)
          reset()
          close()
        },
        navigateOnCreated,
        attributeOptions,
      },
    )
    const { getValues, watch, reset } = methods
    const groups = getDataClassFormMap(dataClass, getValues, watch, {
      attributeOptions,
    })

    const { isProcessing, onValidResult, onProcessingStart, onError } =
      useBusinessCardOcr({
        form: methods,
      })

    useEffect(() => {
      if (attributeOptions) {
        reset()
      }
    }, [attributeOptions, reset])

    const onDrawerClose = () => {
      reset()
      close()
    }

    return (
      <>
        <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
          <DrawerHeader>
            {addCanvasTitle}
            <div className="d-flex">
              {dataClass.name() === DataClassName.PERSON && (
                <BusinessCardScanButton
                  tooltip={t('common.buttons.scanBusinessCard')}
                  tooltipPosition="bottom"
                  onValidResult={onValidResult}
                  onStart={onProcessingStart}
                  onError={onError}
                >
                  <Icon icon="scan" size="lg" />
                </BusinessCardScanButton>
              )}
              <Button
                variant="icon"
                icon="cross"
                tooltip={t('tooltip.close')}
                onClick={onDrawerClose}
              />
            </div>
          </DrawerHeader>
          <DrawerBody>
            <FormProvider {...methods}>
              <Form onSubmit={onFormSubmit}>
                <FormBody>
                  {groups.map((group) => (
                    <FormGroup key={group.name}>
                      <StringText variant="title">{group.title}</StringText>
                      <FormFields>
                        {group.fields.map((field) => (
                          <CustomField
                            key={field.fieldKey}
                            fieldMap={field}
                            isEditing={true}
                            className="form-field"
                            isReadonly={
                              attributeOptions?.[field.fieldKey]?.readOnly
                            }
                          />
                        ))}
                      </FormFields>
                    </FormGroup>
                  ))}
                </FormBody>
              </Form>
            </FormProvider>
          </DrawerBody>
          <DrawerFooter align="right">
            <Button variant="outlined" onClick={onDrawerClose} size="lg">
              {t('common.cancel')}
            </Button>
            <Button icon="disk" onClick={handleSave} size="lg">
              {t('common.save')}
            </Button>
          </DrawerFooter>
        </Drawer>
        <GlobalLoadingSpinner isLoading={isProcessing} />
      </>
    )
  },
)
